import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Compass, Loader2, Sparkles, Target, Zap, PartyPopper, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const CATEGORY_TONE = {
    apply: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    skill: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    passport: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    interview: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    network: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
    salary: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
};
const IMPACT_TONE = {
    high: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    medium: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    low: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
};

export default function CareerCoach() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async (silent = false) => {
        try {
            silent ? setRefreshing(true) : setLoading(true);
            const { data } = await api.get("/professional/career-coach");
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); setRefreshing(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div data-testid="career-coach-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">AI Career Assistant</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Career Coach
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Your personalized weekly action plan — synthesized from your Passport, matches, and platform activity.</p>
                </div>
                <button onClick={() => load(true)} disabled={refreshing} className="btn-outline text-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="cc-refresh">
                    <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Regenerate
                </button>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : !data ? (
                <div className="card-soft p-12 text-center text-muted-foreground">Unable to load.</div>
            ) : (
                <div className="space-y-6">
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="card-soft p-6 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-sky-500/5">
                        <div className="overline text-emerald-600 inline-flex items-center gap-1"><Compass size={13} /> Your positioning</div>
                        <h2 className="mt-1 font-heading font-semibold text-lg text-foreground">{data.plan.headline}</h2>
                        {data.plan.celebrate && (
                            <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 inline-flex items-start gap-2">
                                <PartyPopper size={13} className="text-emerald-500 mt-0.5 shrink-0" />
                                <span className="text-xs text-foreground">{data.plan.celebrate}</span>
                            </div>
                        )}
                    </motion.div>

                    <div>
                        <h3 className="font-heading font-semibold text-foreground mb-3 inline-flex items-center gap-2">
                            <Zap size={14} className="text-emerald-500" /> This week&apos;s actions
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cc-actions">
                            {data.plan.actions.map((a, i) => (
                                <motion.div key={i} initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                                    className="card-soft p-4" data-testid={`cc-action-${i}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-heading font-semibold text-foreground text-sm leading-snug">{a.title}</div>
                                        {a.impact && <Badge className={`text-[10px] border shrink-0 ${IMPACT_TONE[a.impact] || IMPACT_TONE.medium}`}>{a.impact} impact</Badge>}
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">{a.detail}</p>
                                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                        {a.category && <Badge className={`text-[10px] border ${CATEGORY_TONE[a.category] || CATEGORY_TONE.apply}`}>{a.category}</Badge>}
                                        {a.effort && <Badge variant="outline" className="text-[10px] border-border">{a.effort.replace("_", " ")}</Badge>}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {data.plan.focus_roles?.length > 0 && (
                            <div className="card-soft p-5">
                                <div className="overline inline-flex items-center gap-1"><Target size={11} /> Focus roles</div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {data.plan.focus_roles.map((r, i) => (
                                        <Badge key={i} className="text-xs border border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400">{r}</Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        {data.plan.skills_to_add?.length > 0 && (
                            <div className="card-soft p-5">
                                <div className="overline inline-flex items-center gap-1"><Sparkles size={11} /> Skills to add</div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {data.plan.skills_to_add.map((s, i) => (
                                        <Badge key={i} className="text-xs border border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400">{s}</Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="card-soft p-5">
                        <div className="overline mb-2">Your platform activity</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            {Object.entries(data.activity).map(([k, v]) => (
                                <div key={k}>
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{k.replace(/_/g, " ")}</div>
                                    <div className="mono-score font-bold text-foreground text-lg">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
